import { action, observable, runInAction } from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage } from 'react-intl';
import React from 'react';
import { ILinkDTO, IMeetingRequestParams, IMeetingsDAO } from '../../../../../../api/meetings.dao';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../../../infrastructure/alerts';
import { Space } from '../../../../../../constants/html-character-entities';

export type CreateLinkType = Omit<ILinkDTO, 'linkId'>;
export const MAX_LINK_NUMBER = 25;

export enum DefaultLinkType {
    Other = 'Other'
}

export class LinksViewModel {
    @observable
    public links: ILinkDTO[];
    @observable
    public addedLinks: ILinkDTO[];
    private readonly meetingParams: IMeetingRequestParams;
    public readonly setAddedLinks: (link: ILinkDTO) => void;
    public readonly removeAddedLinks: (link: ILinkDTO) => void;
    public readonly getAddedLinks: () => ILinkDTO[];
    constructor(
        props,
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly meetingsDAO: IMeetingsDAO = IoC.Container.get(IMeetingsDAO),
    ) {
        this.meetingParams = { accountId: props.accountId, eventId: props.eventId, meetingId: props.meetingId };
        this.links = props.links;
        this.setAddedLinks = props.setAddedLinks;
        this.removeAddedLinks = props.removeAddedLinks;
        this.getAddedLinks = props.getAddedLinks;
    }

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    public get meetingLinks(): ILinkDTO[] {
        return this.links || [];
    }

    @action.bound
    public onAddLink = async (link: CreateLinkType): Promise<void> => {
        const dto: ILinkDTO = { ...link, type: DefaultLinkType.Other, linkId: null };
        dto.label = link.label.trim();
        this.setAddedLinks(dto);
    };

    @action.bound
    public onDeleteLink = (link: ILinkDTO) => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.userGroups.deleteThisLink'
                            description='Confirm message'
                            defaultMessage='delete the {label} link'
                            values={{ label: link.label || link.url}}
                        />
                    </span>
                    ?
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.userGroups.deleteLink'
                        description='label text'
                        defaultMessage='Delete Link'
                    />
                ),
                modifier: 'confirm-warn',
                handler: () => this.removeLink(link),
            },
            cancelButtonProps: {
                modifier: 'cancel',
            },
        });
    };

    @action.bound
    private removeLink = async (linkForRemoving: ILinkDTO): Promise<void> => {
        try {
            if (linkForRemoving.linkId) await this.meetingsDAO.deleteLink(linkForRemoving.linkId, this.meetingParams);
            runInAction(() => {
                let index = null;
                if (linkForRemoving.linkId) {
                    index = this.links.findIndex(link => link.linkId === linkForRemoving.linkId);
                    if (index > -1) this.links.splice(index, 1);
                } else {
                    this.removeAddedLinks(linkForRemoving);
                }

            });

        } catch (e) {
            LinksViewModel.handleApiException(e);
        }
    };

}
